import { useCallback, useEffect, useRef, useState } from 'react';

import { api, type FelszolitasLista, type FelszolitasSor, type LevelAdat } from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { ft } from '../formazas.ts';
import { ablakotBezar, ablakotNyit, dokumentumMegjelenites } from '../reszek/dokumentum-ablak.ts';
import { LevelKuldoPanel, type LevelKeres } from '../reszek/LevelKuldes.tsx';
import { LEVEL_CSS, levelLap, levelek } from '../reszek/felszolitas-dokumentum.ts';

/**
 * Fizetési felszólítások. (Megbízás 2.0 / 5.6)
 *
 * A lista **cégenként** áll, mert a tartozás a céget terheli, nem az egyes
 * telephelyeket: egy étteremlánc minden egységének nyitott számlája egy
 * levélbe kerül.
 *
 * Kijelöléssel több levél készíthető egyszerre. Akinek van lejárt tétele,
 * fizetési felszólítást kap; akinek csak határidőn belüli nyitott számlája
 * van, egyenlegközlőt.
 */

const SZURO_SORREND = ['lejart', 'nyitott', 'regi', 'nincs_email'] as const;

export function Felszolitasok({ kezdoSzuro }: { kezdoSzuro?: string }) {
  const [kereses, setKereses] = useState('');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState('');
  const [szuro, setSzuro] = useState<string>(kezdoSzuro ?? 'lejart');
  const [lista, setLista] = useState<FelszolitasLista | null>(null);
  const [kijelolt, setKijelolt] = useState<Set<number>>(new Set());
  const [nyitottSor, setNyitottSor] = useState<number | null>(null);
  const [kuldendo, setKuldendo] = useState<LevelKeres | null>(null);
  const kuldhet = useMuvelet('email_kuldes');
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setKeresesKesleltetve(kereses), 220);
    return () => clearTimeout(t);
  }, [kereses]);

  /** Csak a legfrissebb lekérdezés eredménye kerüljön a képernyőre. */
  const kerelemSorszam = useRef(0);

  const betolt = useCallback(async () => {
    const sajat = ++kerelemSorszam.current;
    try {
      const p = new URLSearchParams();
      if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
      p.set('szuro', szuro);
      const eredmeny = await api.get<FelszolitasLista>(`/api/felszolitas?${p}`);
      if (sajat !== kerelemSorszam.current) return;
      setLista(eredmeny);
      setHiba(null);
    } catch (h) {
      if (sajat !== kerelemSorszam.current) return;
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    }
  }, [keresesKesleltetve, szuro]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const emailMent = useCallback(async (ceg_id: number, ertek: string | null) => {
    const frissitett = await api.patch<FelszolitasSor>(`/api/felszolitas/ceg/${ceg_id}/email`, {
      ertek,
    });
    setLista((elozo) =>
      elozo
        ? {
            ...elozo,
            sorok: elozo.sorok.map((s) => (s.ceg_id === ceg_id ? frissitett : s)),
            osszesites: {
              ...elozo.osszesites,
              van_email:
                elozo.osszesites.van_email +
                (frissitett.email ? 1 : 0) -
                (elozo.sorok.find((s) => s.ceg_id === ceg_id)?.email ? 1 : 0),
            },
          }
        : elozo,
    );
  }, []);

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;

  const kijeloltSorok = sorok.filter((s) => kijelolt.has(s.ceg_id));
  const kijeloltOsszeg = kijeloltSorok.reduce((a, s) => a + s.nyitott_osszeg, 0);
  const mindKijelolve = sorok.length > 0 && sorok.every((s) => kijelolt.has(s.ceg_id));

  function valt(ceg_id: number) {
    setKijelolt((elozo) => {
      const uj = new Set(elozo);
      if (uj.has(ceg_id)) uj.delete(ceg_id);
      else uj.add(ceg_id);
      return uj;
    });
  }

  /** A cég levele elkészül, és a küldő panel nyílik meg vele. */
  async function kuldesElokeszites(s: FelszolitasSor) {
    try {
      const valasz = await api.post<{ levelek: LevelAdat[] }>('/api/felszolitas/levelek', {
        ceg_idk: [s.ceg_id],
      });
      const l = valasz.levelek[0];
      if (!l) {
        setHiba('Ennek a cégnek nincs nyitott számlája — nincs mit küldeni.');
        return;
      }
      setKuldendo({
        tipus: 'felszolitas',
        cim: `${l.fajta === 'felszolitas' ? 'Fizetési felszólítás' : 'Egyenlegközlő'} — ${s.ceg_nev}`,
        ceg_id: s.ceg_id,
        cimzett: s.email,
        tabla: 'ceg',
        rekord_id: s.ceg_id,
        adatok: {
          /* A felszólított cég a CÍMZETT. Az aláírásba a saját cégünk megy,
             azt a szerver teszi bele — ide nem adhatjuk át. (2026-10-16.) */
          partner: s.ceg_nev,
          /* Az összeg a levél szövegébe megy: magyar formában, forinttal. */
          osszeg: `${ft(l.osszesen)} Ft`,
          lejart_osszeg: `${ft(l.lejart_osszesen)} Ft`,
          darab: s.nyitott_db,
          kelt: l.kelt,
        },
        html: levelLap(l),
        fajlnev: `${l.fajta === 'felszolitas' ? 'felszolitas' : 'egyenlegkozlo'}-${s.ceg_nev}`,
      });
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A levél nem készült el.');
    }
  }

  async function leveletKeszit(idk: number[]) {
    if (idk.length === 0) return;
    // Ha a böngésző letiltja az ablakot, a levél a felület fölött jelenik meg.
    const ablak = ablakotNyit(LEVEL_CSS);
    try {
      const valasz = await api.post<{ levelek: LevelAdat[]; kihagyva: number }>(
        '/api/felszolitas/levelek',
        { ceg_idk: idk },
      );
      if (valasz.levelek.length === 0) {
        ablakotBezar(ablak);
        setHiba('A kijelölt cégeknek nincs nyitott számlájuk — nincs mit küldeni.');
        return;
      }

      const felsz = valasz.levelek.filter((l) => l.fajta === 'felszolitas').length;
      const egyenleg = valasz.levelek.length - felsz;
      const cim =
        valasz.levelek.length === 1
          ? `${valasz.levelek[0]!.fajta === 'felszolitas' ? 'Fizetési felszólítás' : 'Egyenlegközlő'} — ${valasz.levelek[0]!.cimzett.ceg_nev}`
          : `${valasz.levelek.length} levél`;

      dokumentumMegjelenites(ablak, cim, levelek(valasz.levelek), LEVEL_CSS);

      setUzenet(
        `${valasz.levelek.length} levél elkészült` +
          (egyenleg > 0 ? ` (${felsz} felszólítás, ${egyenleg} egyenlegközlő)` : '') +
          (valasz.kihagyva > 0
            ? `. ${valasz.kihagyva} cég kimaradt, mert nincs nyitott számlája.`
            : '.'),
      );
    } catch (h) {
      ablakotBezar(ablak);
      setHiba(h instanceof Error ? h.message : 'A levél nem készült el.');
    }
  }

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Fizetési felszólítások</span>
        <input
          className="qi"
          style={{ width: 240, fontSize: 12.5 }}
          placeholder="cég keresése név vagy adószám alapján…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
        <div className="stats">
          <div>
            Érintett cég
            <strong>{ft(o?.erintett_ceg ?? 0)}</strong>
          </div>
          <div>
            Lejárt tartozás
            <strong>{ft(o?.lejart_osszeg ?? 0)} Ft</strong>
          </div>
          <div>
            E-mail cím
            <strong>
              {ft(o?.van_email ?? 0)} / {ft(o?.ceg_osszesen ?? 0)}
            </strong>
          </div>
        </div>
      </div>

      <div className="fchips">
        {SZURO_SORREND.map((k) => (
          <button
            key={k}
            className={`fchip${szuro === k ? ' on' : ''}`}
            title={
              k === 'regi'
                ? `${lista?.regi_hatar ?? 30} napnál régebben lejárt tétel`
                : undefined
            }
            onClick={() => {
              setSzuro(k);
              setKijelolt(new Set());
            }}
          >
            {lista?.szuro_nevek[k] ?? k}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {/* A helyszínen fizetett számlákból nem lehet felszólítás. (2026-09-25) */}
      <span className="hint">
        A készpénzes és a bankkártyás számlák nem szerepelnek itt: azokat a vendéglő a
        helyszínen kifizette. A kártyás pénz a POS-elszámolással érkezik, jellemzően 1–3 nap
        múlva — ha valamelyik mégsem futna be, az a Bankkivonatok képernyőn derül ki.
      </span>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {kuldendo && (
        <LevelKuldoPanel
          keres={kuldendo}
          onKesz={(u) => {
            setKuldendo(null);
            setUzenet(u);
          }}
          onMegse={() => setKuldendo(null)}
        />
      )}

      {kijelolt.size > 0 && (
        <div className="kiosztsav">
          <span className="kl">Kijelölt</span>
          <span className="kchip akt">
            <b>{ft(kijelolt.size)}</b>
            <i>{ft(kijeloltOsszeg)} Ft nyitott</i>
          </span>
          <button
            className="btn gold sm"
            onClick={() => void leveletKeszit(kijeloltSorok.map((s) => s.ceg_id))}
          >
            Levelek elkészítése
          </button>
          <button className="btn sm" onClick={() => setKijelolt(new Set())}>
            Kijelölés törlése
          </button>
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 600, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 30, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={mindKijelolve}
                  title="Mind kijelölése"
                  onChange={(e) =>
                    setKijelolt(e.target.checked ? new Set(sorok.map((s) => s.ceg_id)) : new Set())
                  }
                />
              </th>
              <th>Cég</th>
              <th className="n" style={{ width: 64 }}>
                Nyitott
              </th>
              <th className="n" style={{ width: 56 }}>
                Lejárt
              </th>
              <th className="n" style={{ width: 110 }}>
                Nyitott összeg
              </th>
              <th className="n" style={{ width: 110 }}>
                Lejárt összeg
              </th>
              <th className="n" style={{ width: 84 }}>
                Legrégebbi
              </th>
              <th style={{ minWidth: 190 }}>E-mail</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <FelszolitasSorNezet
                key={s.ceg_id}
                s={s}
                regiHatar={lista?.regi_hatar ?? 30}
                kijelolve={kijelolt.has(s.ceg_id)}
                nyitva={nyitottSor === s.ceg_id}
                onValt={() => valt(s.ceg_id)}
                onNyit={() => setNyitottSor(nyitottSor === s.ceg_id ? null : s.ceg_id)}
                onEmail={emailMent}
                onLevel={() => void leveletKeszit([s.ceg_id])}
                onKuldes={kuldhet ? () => void kuldesElokeszites(s) : undefined}
              />
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {lista === null ? 'Betöltés…' : 'Nincs ilyen cég.'}
                </td>
              </tr>
            )}
          </tbody>
          {sorok.length > 0 && (
            <tfoot>
              <tr>
                <td></td>
                <td className="dcol">{ft(sorok.length)} cég</td>
                <td className="calc2">{ft(sorok.reduce((a, s) => a + s.nyitott_db, 0))}</td>
                <td className="calc2">{ft(sorok.reduce((a, s) => a + s.lejart_db, 0))}</td>
                <td className="calc2">{ft(sorok.reduce((a, s) => a + s.nyitott_osszeg, 0))}</td>
                <td className="calc2">{ft(sorok.reduce((a, s) => a + s.lejart_osszeg, 0))}</td>
                <td></td>
                <td style={{ fontSize: 11, textAlign: 'left', textTransform: 'none' }}>
                  {ft(sorok.filter((s) => s.email).length)} / {ft(sorok.length)} címmel
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="uzenet flag" style={{ marginTop: 14 }}>
        <b>E-mail küldés nincs a rendszerben.</b> A levél nyomtatható és PDF-be menthető; a
        kiküldés a saját levelezőprogramban történik. A címeket itt, a táblázatban lehet pótolni.
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function FelszolitasSorNezet({
  s,
  regiHatar,
  kijelolve,
  nyitva,
  onValt,
  onNyit,
  onEmail,
  onLevel,
  onKuldes,
}: {
  s: FelszolitasSor;
  regiHatar: number;
  kijelolve: boolean;
  nyitva: boolean;
  onValt: () => void;
  onNyit: () => void;
  onEmail: (ceg_id: number, ertek: string | null) => Promise<void>;
  onLevel: () => void;
  /** Csak akkor van gomb, ha a felhasználó küldhet levelet. */
  onKuldes?: () => void;
}) {
  // A mezőben a használható cím áll, akkor is, ha az még csak a
  // telephelyé. Ha hozzányúlnak, a cégre mentjük — onnantól az a cég címe.
  const [email, setEmail] = useState(s.email ?? '');
  const [emailHiba, setEmailHiba] = useState<string | null>(null);

  useEffect(() => {
    setEmail(s.email ?? '');
  }, [s.email]);

  async function emailMentes() {
    const uj = email.trim() || null;
    if (uj === (s.ceg_email ?? null)) return;
    try {
      await onEmail(s.ceg_id, uj);
      setEmailHiba(null);
    } catch (h) {
      setEmailHiba(h instanceof Error ? h.message : 'Nem sikerült menteni.');
      setEmail(s.email ?? '');
    }
  }

  return (
    <>
      <tr className={kijelolve ? 'kijelolt' : undefined}>
        <td style={{ textAlign: 'center' }}>
          <input type="checkbox" checked={kijelolve} onChange={onValt} />
        </td>
        <td className="nm3" onClick={onNyit} title="Nyitott tételek megnyitása">
          <b>{s.ceg_nev}</b>
          {s.telephelyek > 1 && <small>{s.telephelyek} telephely</small>}
        </td>
        <td className="calc2">{s.nyitott_db || '·'}</td>
        <td className={`calc2${s.lejart_db > 0 ? ' bad2' : ''}`}>{s.lejart_db || '·'}</td>
        <td className="calc2">{s.nyitott_osszeg ? ft(s.nyitott_osszeg) : '·'}</td>
        <td className="calc2" style={{ fontWeight: 600 }}>
          {s.lejart_osszeg ? ft(s.lejart_osszeg) : '·'}
        </td>
        <td className={`calc2${s.legnagyobb_keses > regiHatar ? ' bad2' : ''}`}>
          {s.legnagyobb_keses ? `${s.legnagyobb_keses} nap` : '·'}
        </td>
        <td>
          {/*
            * Ha a cím a telephelyek **számlázási** kategóriájából jön, itt
            * nem szerkeszthető: azt a partnernél állítják be, és a
            * felszólítás onnan veszi. Enélkül a mentés a cégre írt volna
            * egy másolatot, ami ettől kezdve két helyen áll.
            * (2026-10-07, a megrendelő kérése)
            */}
          <input
            className={`emin${s.email_forras === 'ceg' ? '' : ' orokolt'}`}
            value={email}
            readOnly={s.email_forras === 'telephely_szamla'}
            placeholder="nincs megadva"
            title={
              emailHiba ??
              (s.email_forras === 'telephely_szamla'
                ? 'A telephelyek számlázási címe. A partner adatlapján módosítható.'
                : s.email_forras === 'telephely'
                  ? 'Ez a telephely kategória nélküli címe. Ha átírod vagy elmented, a cég saját címe lesz.'
                  : undefined)
            }
            style={emailHiba ? { borderColor: 'var(--bad)' } : undefined}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => void emailMentes()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setEmail(s.email ?? '');
                setEmailHiba(null);
              }
            }}
          />
        </td>
        <td className="act">
          <button
            className="btn sm"
            title={s.lejart_db > 0 ? 'Fizetési felszólítás' : 'Egyenlegközlő'}
            disabled={s.nyitott_db === 0}
            onClick={onLevel}
          >
            Levél
          </button>
          {onKuldes && (
            <button
              className="btn sm"
              title="A levél elküldése e-mailben"
              disabled={s.nyitott_db === 0}
              onClick={onKuldes}
            >
              Küldés
            </button>
          )}
        </td>
      </tr>

      {nyitva && (
        <tr>
          <td colSpan={9} style={{ padding: 0 }}>
            <div className="tetellista">
              <table className="tt2">
                <thead>
                  <tr>
                    <th>Bizonylat</th>
                    <th>Telephely</th>
                    <th className="n" style={{ width: 96 }}>
                      Kelt
                    </th>
                    <th className="n" style={{ width: 96 }}>
                      Határidő
                    </th>
                    <th className="n" style={{ width: 110 }}>
                      Bruttó
                    </th>
                    <th className="n" style={{ width: 84 }}>
                      Késés
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {s.tetelek.map((t) => (
                    <tr key={t.id}>
                      <td>{t.bizonylatszam}</td>
                      <td>{t.telephely ?? '—'}</td>
                      <td className="calc2">{t.kelt ?? '—'}</td>
                      <td className="calc2">{t.fizetesi_hatarido ?? '—'}</td>
                      <td className="calc2">{ft(t.brutto)}</td>
                      <td className={`calc2${t.keses > 0 ? ' bad2' : ''}`}>
                        {t.keses > 0 ? `${t.keses} nap` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
